import {
  Anchor,
  Badge,
  Box,
  Card,
  Center,
  Group,
  Loader,
  Progress,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getLLMLimits, getLLMLog, type LLMLimitState, type LLMLogEntry } from '../lib/api.js';

function fmtCountdown(resetMs: number | null): string {
  if (!resetMs) return '—';
  const s = Math.max(0, Math.round((resetMs - Date.now()) / 1000));
  if (s === 0) return 'jetzt';
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function fmtClock(ts: number): string {
  return new Date(ts).toLocaleTimeString('de-AT', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function statusBadgeColor(s: LLMLogEntry['status']): string {
  if (s === 'ok') return 'teal';
  if (s === 'rate-limited') return 'orange';
  if (s === 'parse-error') return 'yellow';
  return 'red';
}

function utilizationPct(remaining: number | null, limit: number | null): number {
  if (!limit || remaining === null) return 0;
  return Math.max(0, Math.min(100, ((limit - remaining) / limit) * 100));
}

function barColorForUsage(pct: number): string {
  if (pct < 60) return 'teal';
  if (pct < 85) return 'yellow';
  return 'red';
}

export function LLMStatus(): JSX.Element {
  const limitsQuery = useQuery({
    queryKey: ['llm-limits'],
    queryFn: getLLMLimits,
    refetchInterval: 3000,
  });
  const logQuery = useQuery({
    queryKey: ['llm-log'],
    queryFn: () => getLLMLog(60),
    refetchInterval: 5000,
  });

  const models = limitsQuery.data?.models ?? [];
  const entries = logQuery.data?.entries ?? [];

  return (
    <Stack gap="xl">
      <Stack gap={4}>
        <Anchor component={Link} to="/health" size="sm" c="dimmed">
          ← Übersicht
        </Anchor>
        <Title order={1}>LLM-Status</Title>
        <Text c="dimmed" size="sm">
          Rate-Limits live aus den Response-Headern · Refresh alle 3 Sek
        </Text>
      </Stack>

      {/* Rate Limits per Modell */}
      <Stack gap="md">
        <Title order={3}>Rate-Limits pro Modell</Title>
        {limitsQuery.isLoading && (
          <Center py="xl">
            <Loader />
          </Center>
        )}
        {!limitsQuery.isLoading && models.length === 0 && (
          <Card padding="lg">
            <Text c="dimmed" size="sm">
              Noch keine Daten — sobald der erste LLM-Call läuft, erscheinen die Limits hier.
            </Text>
          </Card>
        )}
        {models.map((m) => (
          <ModelLimitCard key={`${m.provider}:${m.model}`} m={m} />
        ))}
      </Stack>

      {/* Call-Log */}
      <Stack gap="md">
        <Title order={3}>Letzte LLM-Aufrufe</Title>
        {entries.length === 0 ? (
          <Card padding="lg">
            <Text c="dimmed" size="sm">
              Noch keine Aufrufe.
            </Text>
          </Card>
        ) : (
          <Card padding="sm">
            <Stack gap={0}>
              {entries.map((e, i) => (
                <LogRow key={e.id} entry={e} striped={i % 2 === 1} />
              ))}
            </Stack>
          </Card>
        )}
      </Stack>
    </Stack>
  );
}

function ModelLimitCard({ m }: { m: LLMLimitState }): JSX.Element {
  const tokPct = utilizationPct(m.remainingTokensPerMin, m.limitTokensPerMin);
  const reqPct = utilizationPct(m.remainingRequestsPerMin, m.limitRequestsPerMin);
  const noData = m.lastUpdatedAt === 0;

  return (
    <Card padding="lg">
      <Stack gap="sm">
        <Group justify="space-between" align="baseline" wrap="wrap">
          <Group gap="xs" align="baseline">
            <Badge
              size="sm"
              color={m.provider === 'groq' ? 'violet' : 'blue'}
              variant="light"
              tt="none"
            >
              {m.provider}
            </Badge>
            <Text fw={600} style={{ fontSize: '0.95rem' }}>
              {m.model}
            </Text>
            {m.lastCallStatus && (
              <Badge size="xs" color={statusBadgeColor(m.lastCallStatus)} variant="dot" tt="none">
                {m.lastCallStatus}
              </Badge>
            )}
          </Group>
          <Text size="xs" c="dimmed">
            zuletzt aktualisiert {noData ? '—' : fmtClock(m.lastUpdatedAt)}
          </Text>
        </Group>

        {noData ? (
          <Text c="dimmed" size="sm" fs="italic">
            Noch keine Anfrage an dieses Modell → keine Header bisher.
          </Text>
        ) : (
          <>
            <Stack gap={4}>
              <Group justify="space-between" gap="xs">
                <Text size="sm">
                  Tokens/Min · {m.remainingTokensPerMin ?? '—'} / {m.limitTokensPerMin ?? '—'}
                </Text>
                <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  Reset in {fmtCountdown(m.resetTokensMs)}
                </Text>
              </Group>
              <Progress value={tokPct} color={barColorForUsage(tokPct)} size="sm" radius="sm" />
            </Stack>

            <Stack gap={4}>
              <Group justify="space-between" gap="xs">
                <Text size="sm">
                  Requests/Min · {m.remainingRequestsPerMin ?? '—'} / {m.limitRequestsPerMin ?? '—'}
                </Text>
                <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  Reset in {fmtCountdown(m.resetRequestsMs)}
                </Text>
              </Group>
              <Progress value={reqPct} color={barColorForUsage(reqPct)} size="sm" radius="sm" />
            </Stack>

            {m.lastRateLimitedAt && Date.now() - m.lastRateLimitedAt < 5 * 60_000 && (
              <Text size="xs" c="orange.7">
                ⚠ zuletzt rate-limited um {fmtClock(m.lastRateLimitedAt)}
              </Text>
            )}
          </>
        )}
      </Stack>
    </Card>
  );
}

function LogRow({ entry, striped }: { entry: LLMLogEntry; striped: boolean }): JSX.Element {
  return (
    <Box
      style={{
        padding: '6px 10px',
        background: striped ? 'rgba(0,0,0,0.02)' : 'transparent',
        borderRadius: 4,
      }}
    >
      <Group gap="sm" wrap="wrap" align="baseline">
        <Text size="xs" c="dimmed" style={{ fontFamily: 'JetBrains Mono, monospace', minWidth: 64 }}>
          {fmtClock(entry.timestamp)}
        </Text>
        <Badge size="xs" color={statusBadgeColor(entry.status)} variant="light" tt="none">
          {entry.status}
        </Badge>
        <Badge size="xs" variant="light" color="gray" tt="none">
          {entry.provider}
        </Badge>
        <Text size="xs" style={{ minWidth: 180 }}>
          {entry.model}
        </Text>
        {entry.objectId !== null && (
          <Anchor
            component={Link}
            to={`/objects/${entry.objectId}`}
            size="xs"
            c="indigo.6"
          >
            obj {entry.objectId}
          </Anchor>
        )}
        {entry.promptTokens !== null && (
          <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {entry.promptTokens} → {entry.completionTokens ?? 0} tok
          </Text>
        )}
        <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {entry.durationMs}ms
        </Text>
        {entry.error && (
          <Text size="xs" c="red.7" style={{ wordBreak: 'break-word', flex: 1, minWidth: 100 }}>
            {entry.error.substring(0, 120)}
          </Text>
        )}
      </Group>
    </Box>
  );
}
